import './RtuTreeList.css'; // Importing CSS for styling

import { AppDispatch, RootState } from '../../redux/store/store';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Avatar } from 'primereact/avatar';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { RTU } from '../../types/RTU';
import RTUActions from '../RTUActions';
import { SkeletonRegion, SkeletonTree } from '../common/Skeleton/Skeleton';
import { Tag } from 'primereact/tag';
import { TreeTable } from 'primereact/treetable';
import createTreeData, { detachCrossGroupChildren, UNASSIGNED_GROUP_LABEL } from './TreeData/createTreeData';
import { isMissing } from '../../utils/rtuDefaults';
import { defaultThemeColor } from '../../config/themeConfig';
import { fetchRTUs } from '../../redux/slices/rtuSlice';
import { buildAssetStatusFilterOptions, getDisplayStatusFromRecord, getStatusColor, getStatusLabelKey } from '../../utils/statusConfig';
import { humanizeStatusReason, humanizeStatusSource } from '../../utils/statusDisplayConfig';
import { PaginatorRowsLabel, ROWS_PER_PAGE_OPTIONS, STANDARD_PAGE_REPORT_TEMPLATE, STANDARD_PAGINATOR_TEMPLATE } from '../../utils/paginatorConfig';
import { formatDisplayDateTime } from '../../utils/dateTimeFormat';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import TruncatedValue from '../common/TruncatedValue/TruncatedValue';
import BulkSelectionCount from '../RtuCluster/BulkSelectionCount';
import { ClusterNameCell, WellIdCell, WellNameCell } from '../RtuCluster/WellCells';
import { buildRowSearchText, displayedRows, getWellView, hasAnyFormerWells, hasAnyWells, wellColumnText } from '../RtuCluster/rtuCluster';
import {
    buildClusterView,
    ColumnFilters,
    hasActiveColumnFilter,
    makeRowComparator,
    matchesColumnFilters,
} from '../RtuCluster/clusterView';
import { persistToggledColumns, useIsMobileViewport, withMobileColumnDefaults } from '../RtuCluster/mobileColumns';
import {
    ACTIONS_COLUMN_WIDTH,
    cellContent,
    columnMinPx,
    middleEllipsis,
    NO_RESIZER_PT,
    producerExtra,
    resolveColumnWidths,
    useColumnResize,
    useContentWidths,
    useWrapperWidth,
    WidthAccumulator,
    widthStyle,
} from '../RtuCluster/columnLayout';
import { resolveRtuSerialNo } from '../../utils/rtuIdentity';
import { groupKind, nodeDisplayName, selectionAction, selectionAriaState } from './groupedTreeLabels';

const WELL_FIELDS = ['wellName', 'wellId'];

/** Column filters match the raw value; the well columns hold every searched well (see wellColumnText). */
const filterValue = (row: any, field: string): unknown => row[field];

/** Value a column sorts on. Well columns use the well shown in the row (declared primary or record well). */
const sortValue = (row: any, field: string): unknown => {
    if (field === 'wellName') {
        return getWellView(row).shown?.name ?? '';
    }
    if (field === 'wellId') {
        return getWellView(row).shown?.wellId ?? '';
    }
    return row[field];
};

/** Text columns that can be longer than their column: they render through TruncatedValue (full value, copy control). */
const TRUNCATED_TEXT_FIELDS = ['location', 'configVersion', 'deploymentVersion', 'publicIPAddress', 'modemIP', 'ethernetIP', 'groupId', 'field'];
const TRUNCATED_TIMESTAMP_FIELDS = ['statusObservedAtUtc', 'lastFileReceivedAtUtc', 'lastData', 'installDate', 'reportingToServer', 'lastConfigUpdate'];

const COLUMN_VISIBILITY_STORAGE_PREFIX = 'dms_column_visibility';
const GROUPED_ASSET_VIEW_KEY = 'grouped-asset-list';

const resolveColumnVisibilityUserKey = (): string => {
    if (typeof window === 'undefined') {
        return 'anonymous';
    }

    try {
        const rawUser = window.localStorage.getItem('dms_auth_user');
        if (rawUser) {
            const parsed = JSON.parse(rawUser) as Record<string, unknown>;
            const stableIdentity =
                parsed.username ??
                parsed.logins ??
                parsed.email ??
                parsed.id ??
                parsed.employeeId;
            if (stableIdentity !== undefined && stableIdentity !== null) {
                return String(stableIdentity);
            }
        }
    } catch (error) {
        console.warn('Failed to parse dms_auth_user for column persistence key', error);
    }

    return 'anonymous';
};

const getColumnVisibilityStorageKey = (viewKey: string): string =>
    `${COLUMN_VISIBILITY_STORAGE_PREFIX}:${viewKey}:${resolveColumnVisibilityUserKey()}`;

const hydrateColumnVisibility = <T extends { field: string; visible: boolean }>(
    columns: T[],
    storageKey: string
): T[] => {
    if (typeof window === 'undefined') {
        return columns;
    }

    try {
        const stored = window.localStorage.getItem(storageKey);
        if (!stored) {
            return columns;
        }
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        const hydrated = columns.map((column) =>
            Object.prototype.hasOwnProperty.call(parsed, column.field)
                ? { ...column, visible: Boolean(parsed[column.field]) }
                : column
        );
        if (hydrated.every((column) => !column.visible)) {
            console.warn('Stored column visibility hid every column; restoring defaults');
            return columns;
        }
        return hydrated;
    } catch (error) {
        console.warn('Failed to hydrate column visibility settings', error);
        return columns;
    }
};

const COLUMN_FILTER_DELAY_MS = 300; // the TreeTable's own default filter delay

/**
 * The TreeTable's default column filter input has no accessible name (no label, no placeholder). This is the same
 * input and behaviour (uncontrolled, delayed apply, blank clears the filter) with an aria-label; nothing visible is added.
 */
const TreeColumnTextFilter: React.FC<{
    field: string;
    matchMode: string;
    label: string;
    value: unknown;
    onApply: (field: string, value: string, matchMode: string) => void;
}> = ({ field, matchMode, label, value, onApply }) => {
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(
        () => () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        },
        []
    );
    return (
        <InputText
            type="text"
            className="p-column-filter"
            defaultValue={value === null || value === undefined ? '' : String(value)}
            aria-label={label}
            onInput={(e) => {
                const next = (e.target as HTMLInputElement).value;
                if (timer.current) {
                    clearTimeout(timer.current);
                }
                timer.current = setTimeout(() => {
                    timer.current = null;
                    onApply(field, next, matchMode);
                }, COLUMN_FILTER_DELAY_MS);
            }}
        />
    );
};

const RTUTreeList = () => {
    const { t } = useTranslation();
    const { rtus, loading } = useSelector((state: RootState) => state.rtu || {});
    const dispatch = useDispatch<AppDispatch>();
    const columnStorageKeyRef = useRef<string>(getColumnVisibilityStorageKey(GROUPED_ASSET_VIEW_KEY));
    const touchedColumnsRef = useRef<Set<string>>(new Set());
    const isMobile = useIsMobileViewport();
    const resizeFieldsRef = useRef<Array<string | undefined>>([]);
    const rootRef = useRef<HTMLDivElement>(null);
    const contentWidthsRef = useRef<Record<string, number>>({});
    const { widths: columnWidthOverrides, onColumnResizeEnd, onMouseDownCapture, shouldIgnoreSort, forgetWidth } = useColumnResize(
        () => resizeFieldsRef.current,
        (field) => columnMinPx(field, contentWidthsRef.current)
    );
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [statusFilterValue, setStatusFilterValue] = useState<string | null>(null);
    const [collapsedMasters, setCollapsedMasters] = useState<Set<number>>(() => new Set());
    const [sortField, setSortField] = useState<string | undefined>(undefined);
    const [sortOrder, setSortOrder] = useState<0 | 1 | -1 | null | undefined>(undefined);
    // Grouped paging counts provider groups (the TreeTable's top level). A cluster is placed inside its master's
    // field group, so a page boundary can never separate a child from its master.
    const [first, setFirst] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});
    const [includeFormer, setIncludeFormer] = useState(false);
    // A device is shown once: FORMER records whose asset has a CURRENT row are hidden.
    const shownRtus = React.useMemo<RTU[]>(() => displayedRows(Array.isArray(rtus) ? rtus : []), [rtus]);
    const augmentedRows = React.useMemo(
        () =>
            detachCrossGroupChildren(shownRtus).map((rtu: RTU) => {
                const serialNo = resolveRtuSerialNo(rtu) ?? '';
                return {
                    ...rtu,
                    serialNo,
                    ...wellColumnText(rtu, { includeFormer }),
                    searchText: buildRowSearchText({ ...rtu, serialNo }, { includeFormer }),
                };
            }),
        [includeFormer, shownRtus]
    );
    type TableRow = (typeof augmentedRows)[number];
    const hasWells = React.useMemo(() => hasAnyWells(shownRtus), [shownRtus]);
    const hasFormerWells = React.useMemo(() => hasAnyFormerWells(shownRtus), [shownRtus]);
    const hasRecordIdentity = React.useMemo(() => shownRtus.some((rtu: RTU) => rtu.recordState !== null && rtu.recordState !== undefined), [shownRtus]);
    const hasClusterData = React.useMemo(
        () => hasWells || shownRtus.some((rtu: RTU) => rtu.clusterParentId !== null && rtu.clusterParentId !== undefined),
        [hasWells, shownRtus]
    );
    // Cluster placement is kept for every sort, filter and collapse (see buildClusterView).
    const clusterMode = hasClusterData;
    const wellQuery = globalFilter.trim();
    const activeWellQuery = (
        wellQuery ||
        String(columnFilters.wellName?.value ?? '') ||
        String(columnFilters.wellId?.value ?? '')
    ).trim();
    const filterActive = wellQuery !== '' || Boolean(statusFilterValue) || hasActiveColumnFilter(columnFilters);
    const matches = React.useMemo(() => {
        if (!filterActive) {
            return null;
        }
        const needle = wellQuery.toLowerCase();
        return (row: TableRow) =>
            (needle === '' || row.searchText.includes(needle)) &&
            (!statusFilterValue || getDisplayStatusFromRecord(row) === statusFilterValue) &&
            matchesColumnFilters(row, columnFilters, filterValue);
    }, [columnFilters, filterActive, statusFilterValue, wellQuery]);
    const compare = React.useMemo(
        () => makeRowComparator<TableRow>(sortField && sortOrder ? [{ field: sortField, order: sortOrder }] : [], sortValue),
        [sortField, sortOrder]
    );
    const view = React.useMemo(
        () => buildClusterView(augmentedRows, { matches, compare, collapsed: collapsedMasters }),
        [augmentedRows, collapsedMasters, compare, matches]
    );
    const clusterInfo = view.info;
    const fullTreeData = React.useMemo(() => createTreeData(view.rows, { info: view.info }), [view]);
    const treeFirst = Math.min(first, Math.max(0, Math.floor((fullTreeData.length - 1) / pageSize) * pageSize));
    const treeData = React.useMemo(
        () => fullTreeData.slice(treeFirst, treeFirst + pageSize),
        [fullTreeData, pageSize, treeFirst]
    );
    const toggleCollapsed = useCallback((id: number) => {
        setCollapsedMasters((previous) => {
            const next = new Set(previous);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);
    const rtuMap = React.useMemo(() => {
        const map: { [key: number]: RTU } = {};
        shownRtus.forEach((rtu: RTU) => {
            map[rtu.id] = rtu;
        });
        return map;
    }, [shownRtus]);
    
    const toggleFilters = () => {
        setShowFilters((previous) => {
            const next = !previous;
            setColumns((currentColumns) =>
                currentColumns.map((column) => ({
                    ...column,
                    filter: next,
                }))
            );
            return next;
        });
    };
    const [showFilters, setShowFilters] = useState(false); // Ensure filters are shown
    const [selectedNodeKeys, setSelectedNodeKeys] = useState(null);
    // Phones hide the checkbox selection and bulk actions until the compact Select mode is switched on.
    const [selectMode, setSelectMode] = useState(false);
    const showSelection = !isMobile || selectMode;
    const toggleSelectMode = () => {
        if (selectMode) {
            setSelectedNodeKeys(null);
        }
        setSelectMode(!selectMode);
    };



    const statusOptions = useMemo(() => buildAssetStatusFilterOptions(t), [t]);

    // State for dialog visibility and selected file

    const selectedRows = React.useMemo(() => {
        const selectedKeys = Object.keys(selectedNodeKeys ?? {});
        return selectedKeys
            .map((key) => Number(key))
            .filter((id) => Number.isFinite(id) && Boolean(rtuMap[id]))
            .map((id) => rtuMap[id]);
    }, [rtuMap, selectedNodeKeys]);

      const formatDateTime = (value: string | null | undefined) => formatDisplayDateTime(value);

      const dateTemplate = (rowData: any) => formatDateTime(rowData.lastData);

    const diskUsageBodyTemplate = (rowData:any) => {
      return `${rowData.diskUsage.toFixed(1)}%`;
    };
    const cpuUsageBodyTemplate = (rowData:any) => {
      return `${rowData.cpuUsage.toFixed(1)}%`;
    };
    const getSeverity = (status:any) => {    
        return getStatusColor(status);
    };

    const statusTooltip = (rowData: RTU) => {
        const source = humanizeStatusSource(rowData.statusSource);
        const reason = rowData.statusReason ? ` - ${humanizeStatusReason(rowData.statusReason)}` : '';
        const statusLabel = t(getStatusLabelKey(rowData.status), { defaultValue: String(rowData.status ?? '') });

        return `${statusLabel} (${source})${reason}`;
    };

    const statusItemTemplate = (option: any) => {
        return (
            <Tag
                className="asset-status-filter-tag"
                value={option.label}
                style={{ backgroundColor: getSeverity(option.value) }}
            />
        );
    };

    const statusValueTemplate = (option: any) => {
        if (!option) {
            return <span />;
        }

        return (
            <Tag
                className="asset-status-filter-tag asset-status-filter-tag--value"
                value={option.label}
                style={{ backgroundColor: getSeverity(option.value) }}
            />
        );
    };

    const statusBodyTemplate = (rowData:any) => {
        const displayStatus = rowData.systemRestart === true ? 'rebooting' : rowData.status;
        const tooltip = statusTooltip(rowData as RTU);

        return (
            <span title={tooltip} aria-label={tooltip}>
                <Avatar style={{ backgroundColor: getSeverity(displayStatus), width:'20px', height:'20px'}} shape="circle" />
            </span>
        );
    };
    const rowButtonTemplate = (rowData: any) => {
        if (!rowData.children || rowData.children.length === 0) {
            // Only show the button for leaf nodes (RTUs)
            return (
                <div style={{ display: 'flex', justifyContent: 'center' }} onClick={(event) => event.stopPropagation()}>
                    <RTUActions
                        mode="row"
                        selectedRTUIp={rowData.data.publicIPAddress}
                        selectedRows={[rowData.data]}
                    />
                </div>
            );
        }
        return null;
    };
//     const nodeTemplate = (node: any, column: string) => {
//       if (!node.data.id) {
//           return node.data[column];
//       }
//       switch (column) {
//           case 'name':
//               return (
//                   <span
//                       style={{ cursor: 'pointer', color: 'blue' }}
//                       onClick={() => navigate(`/rtupage/${node.data.id}`)}
//                   >
//                       {node.data.name}
//                   </span>
//               );
//           case 'status':
//               return statusBodyTemplate(node.data);
//           case 'diskUsage':
//               return diskUsageBodyTemplate(node.data);
//           case 'cpuUsage':
//               return cpuUsageBodyTemplate(node.data);
//           default:
//               return node.data[column];
//       }
//   };

const columnLabel = (field: string) => String(columns.find((col) => col.field === field)?.header ?? field);

const nodeTemplate = (node: any, column: string) => {
    const depth = node.key.split('-').length - 1; // Calculate depth based on key

    const style = {
        textAlign: depth < 2 ? 'left' as const : 'center' as const, // Align left for first two levels
        paddingLeft: depth === 1 ? '20px' : '0px', // Add padding for level 2 (IGAPO)
    };

    if (!node.data.id) {
        if (nameFirst && column === 'name') {
            // Phone: the name column is the first (expander) column, so a group row shows its label there.
            return (
                <div className="rtu-tree-trunc rtu-tree-first rtu-tree-first--group">
                    <TruncatedValue
                        value={node.data.provider}
                        label={
                            groupKind(node) === 'field'
                                ? t('assetList.columns.field', { defaultValue: 'Field' })
                                : t('assetList.columns.provider')
                        }
                    />
                </div>
            );
        }
        if (nameFirst && column === 'provider') {
            return null; // the group label is already in the first column
        }
        if (column === 'provider' && node.data.provider) {
            // Desktop: the group (producer / field) label shares the expander cell with the toggler and the checkbox, so its
            // box leaves room for both; a long label ends in an ellipsis and stays available through hover / focus / copy.
            const reserved = 72 + depth * 16;
            return (
                <div className="rtu-tree-group-label" style={{ maxWidth: `calc(100% - ${reserved}px)` }}>
                    <TruncatedValue
                        value={node.data.provider}
                        label={
                            groupKind(node) === 'field'
                                ? t('assetList.columns.field', { defaultValue: 'Field' })
                                : t('assetList.columns.provider')
                        }
                    />
                </div>
            );
        }
        return node.data[column];
    }

    if (TRUNCATED_TEXT_FIELDS.includes(column) || TRUNCATED_TIMESTAMP_FIELDS.includes(column)) {
        const raw = node.data[column];
        const text = TRUNCATED_TIMESTAMP_FIELDS.includes(column) ? formatDateTime(raw) : raw;
        return (
            <div className="rtu-tree-cell" style={style}>
                <TruncatedValue value={text} label={columnLabel(column)} />
            </div>
        );
    }

    switch (column) {
        case 'provider':
            return <span style={style} />;
        case 'name': {
            const former = node.data.recordState === 'FORMER';
            const info = clusterMode && !former ? clusterInfo[Number(node.data.id)] : undefined;
            return (
                <div
                    className={nameFirst ? 'rtu-tree-trunc rtu-tree-first' : 'rtu-tree-trunc'}
                    style={{ ...style, cursor: 'pointer' }}
                    // In phone Select mode a tap on the name only selects; it never navigates by accident.
                    onClick={() => { if (!(isMobile && selectMode)) { navigate(`/rtupage/${node.data.id}`); } }}
                >
                    <ClusterNameCell
                        name={node.data.name}
                        columnLabel={columnLabel('name')}
                        depth={info?.depth ?? 0}
                        slot={clusterMode}
                        isLastChild={info?.isLastChild ?? false}
                        childCount={info?.childCount ?? 0}
                        expanded={info?.expanded ?? false}
                        onToggle={() => toggleCollapsed(Number(node.data.id))}
                        onOpenDetails={() => navigate(`/rtupage/${node.data.id}`)}
                        marker={former ? 'former' : node.data.recordState === 'UNRESOLVED' ? 'unresolved' : undefined}
                        linkStyle
                    />
                </div>
            );
        }
        case 'serialNo':
            return (
                <div className="rtu-tree-trunc rtu-tree-trunc--serial" style={style}>
                    <TruncatedValue value={node.data.serialNo} shortDisplay={middleEllipsis(String(node.data.serialNo ?? ''))} label={columnLabel('serialNo')} />
                </div>
            );
        case 'wellName':
            return (
                <div className="rtu-tree-trunc" style={style}>
                    <WellNameCell
                        rtu={node.data}
                        columnLabel={columnLabel('wellName')}
                        query={activeWellQuery}
                        resolveRtu={(id) => {
                            const found = rtuMap[id];
                            return found ? { id: found.id, name: found.name } : undefined;
                        }}
                    />
                </div>
            );
        case 'wellId':
            return (
                <div className="rtu-tree-trunc rtu-tree-trunc--well-id" style={style}>
                    <WellIdCell rtu={node.data} columnLabel={columnLabel('wellId')} query={activeWellQuery} />
                </div>
            );
        case 'status':
            return statusBodyTemplate(node.data);
        case 'diskUsage':
            return diskUsageBodyTemplate(node.data);
        case 'cpuUsage':
            return cpuUsageBodyTemplate(node.data);
        default:
            return <span style={style}>{node.data[column]}</span>;
    }
};


    /**
     * Accessible names for the expander column's built-in controls (PrimeReact names neither per row):
     *  - toggler: `togglerTemplate` receives the node, so the default button is kept (class, click, keyboard, style)
     *    and only gains "Expand <name>" / "Collapse <name>" and aria-expanded.
     *  - checkbox: the Column `pt.rowCheckbox` callback is the only supported hook to the row checkbox's <input>
     *    (through the Checkbox `pt.input`), but PrimeReact hands it no node. In PrimeReact 10.7 the TreeTable row's
     *    `createCell` (treetable.cjs.js) creates the expander cell's toggler (which calls `togglerTemplate`) and then its
     *    checkbox (which evaluates the Column `pt.rowCheckbox`) one after the other, synchronously, for the same row.
     *    So the toggler template records the node and the checkbox callback of the same row reads it. Nothing in the
     *    callback proves that: the checked / partially-checked state it compares against the recorded node's selection
     *    entry only filters out an inconsistent pair, and two rows with the same state would not be told apart. The naming
     *    therefore depends entirely on that call order, and the real-component tests (they assert every row's name, with
     *    mixed checked / partial / unchecked states) are what guards it: a PrimeReact upgrade that changes the order or
     *    makes it asynchronous fails them loudly.
     */
    const expanderNodeRef = useRef<any>(null);
    const togglerTemplate = (node: any, options: any) => {
        expanderNodeRef.current = node;
        const hasChildren = Array.isArray(node?.children) && node.children.length > 0;
        if (!hasChildren || !React.isValidElement(options?.element)) {
            return options.element;
        }
        const action = options.expanded
            ? t('rtuCluster.collapse', { defaultValue: 'Collapse' })
            : t('rtuCluster.expand', { defaultValue: 'Expand' });
        return React.cloneElement(options.element as React.ReactElement<any>, {
            'aria-label': `${action} ${nodeDisplayName(node)}`.trim(),
            'aria-expanded': Boolean(options.expanded),
        });
    };
    const rowCheckboxPt = (options: any) => {
        const node = expanderNodeRef.current;
        if (!node) {
            return undefined;
        }
        const entry = (selectedNodeKeys as Record<string, { checked?: boolean; partialChecked?: boolean }> | null)?.[node.key];
        const checked = Boolean(entry?.checked);
        const partialChecked = Boolean(entry?.partialChecked);
        if (checked !== Boolean(options?.context?.checked) || partialChecked !== Boolean(options?.context?.partialChecked)) {
            return undefined;
        }
        const name = nodeDisplayName(node);
        const label = {
            selectRtu: t('assetList.selectRtuName', { name, defaultValue: 'Select {{name}}' }),
            deselectRtu: t('assetList.deselectRtuName', { name, defaultValue: 'Deselect {{name}}' }),
            selectGroup: t('assetList.selectGroupRtus', { name, defaultValue: 'Select all RTUs in {{name}}' }),
            deselectGroup: t('assetList.deselectGroupRtus', { name, defaultValue: 'Deselect all RTUs in {{name}}' }),
        }[selectionAction(node, checked)];
        return { pt: { input: { 'aria-label': label, 'aria-checked': selectionAriaState(checked, partialChecked) } } };
    };

    const applyColumnTextFilter = (field: string, value: string, matchMode: string) => {
        setColumnFilters((current) => {
            const next = { ...current };
            if (value === '' || value.trim() === '') {
                delete next[field];
            } else {
                next[field] = { value, matchMode } as ColumnFilters[string];
            }
            return next;
        });
        setFirst(0);
    };
    const columnTextFilter = (field: string, header: unknown, matchMode?: string) => (
        <TreeColumnTextFilter
            field={field}
            matchMode={matchMode || 'startsWith'}
            value={columnFilters[field]?.value}
            label={t('assetList.filterColumn', {
                column: typeof header === 'string' && header.trim() ? header : field,
                defaultValue: 'Filter {{column}}',
            })}
            onApply={applyColumnTextFilter}
        />
    );

    const statusFilterElement = (
        <Dropdown
            value={statusFilterValue}
            options={statusOptions}
            onChange={(e: any) => {
                setStatusFilterValue(e.value ?? null);
                setFirst(0);
            }}
            itemTemplate={statusItemTemplate}
            valueTemplate={statusValueTemplate}
            optionLabel="label"
            optionValue="value"
            placeholder=""
            ariaLabel={t('assetList.filterColumn', { column: t('assetList.columns.status'), defaultValue: 'Filter {{column}}' })}
            aria-label={t('assetList.filterColumn', { column: t('assetList.columns.status'), defaultValue: 'Filter {{column}}' })}
            className="p-column-filter asset-status-filter-dropdown"
            panelClassName="asset-status-filter-panel"
            showClear
            style={{ width: '100%', minWidth: '4rem' }}
        />
    );

    const [columns, setColumns] = useState(() => withMobileColumnDefaults([
    //   { field: 'provider', header: 'Producer', headerClassName: 'custom-header-provider', visible: true, filter: true, filterElement: null, body: null, expander: true, filterMatchMode: 'contains' },
      // ID column hidden for the cluster layout: the well ID is already shown in "Primary well" as "name [ID]".
      // { field: 'id', header: t('assetList.columns.id'), headerClassName: 'custom-header-id', visible: true, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains'},
      { field: 'status', header: t('assetList.columns.status'), headerClassName: 'custom-header-status', visible: true, filter: true, filterElement: null, body: statusBodyTemplate, expander: false, filterMatchMode: 'equals' },
      { field: 'name', header: t('assetList.columns.rtuName', { defaultValue: 'RTU name' }), headerClassName: 'custom-header-name', visible: true, filter: true, filterElement: null, body: nodeTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'serialNo', header: t('assetList.columns.serialNo', { defaultValue: 'Serial no.' }), headerClassName: 'custom-header-serial-no', visible: true, filter: true, filterElement: null, body: nodeTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'wellName', header: t('assetList.columns.wellName', { defaultValue: 'Wells' }), headerClassName: 'custom-header-well-name', visible: true, filter: true, filterElement: null, body: nodeTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'wellId', header: t('assetList.columns.wellId', { defaultValue: 'Well ID' }), headerClassName: 'custom-header-well-id', visible: true, filter: true, filterElement: null, body: nodeTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'diskUsage', header: t('assetList.columns.diskUsage'), headerClassName: 'custom-header-disk-usage', visible: true, filter: true, filterElement: null, body: diskUsageBodyTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'cpuUsage', header: t('assetList.columns.cpuUsage'), headerClassName: 'custom-header-cpu-usage', visible: true, filter: true, filterElement: null, body: cpuUsageBodyTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'statusObservedAtUtc', header: t('assetList.columns.lastChecked', { defaultValue: 'Last Checked' }), headerClassName: 'custom-header-last-checked', visible: true, filter: true, filterElement: null, body: dateTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'lastFileReceivedAtUtc', header: t('assetList.columns.lastDataReceived', { defaultValue: 'Last Data Received' }), headerClassName: 'custom-header-last-data-received', visible: true, filter: true, filterElement: null, body: dateTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'location', header: t('assetList.columns.location'), headerClassName: 'custom-header-location', visible: true, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'configVersion', header: t('assetList.columns.configVersion'), headerClassName: 'custom-header-config-version', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'deploymentVersion', header: t('assetList.columns.deploymentVersion'), headerClassName: 'custom-header-deployment-version', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'lastData', header: t('assetList.columns.lastData'), headerClassName: 'custom-header-last-data', visible: false, filter: true, filterElement: null, body: dateTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'installDate', header: t('assetList.columns.installDate'), headerClassName: 'custom-header-install-date', visible: false, filter: true, filterElement: null, body: dateTemplate, expander: false, filterMatchMode: 'contains' },
      { field: 'lastConfigUpdate', header: t('assetList.columns.lastConfigUpdate'), headerClassName: 'custom-header-last-config-update', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'publicIPAddress', header: t('assetList.columns.publicIp'), headerClassName: 'custom-header-public-ipaddress', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'reportingToServer', header: t('assetList.columns.reportingToServer'), headerClassName: 'custom-header-reportingtoserver', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'modemIP', header: t('assetList.columns.modemIp'), headerClassName: 'custom-header-public-ipaddress', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'ethernetIP', header: t('assetList.columns.ethernetIp'), headerClassName: 'custom-header-public-ipaddress', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
      { field: 'groupId', header: t('assetList.columns.groupId'), headerClassName: 'custom-header-group-id', visible: false, filter: true, filterElement: null, body: null, expander: false, filterMatchMode: 'contains' },
  ]));
  

    // Well columns exist once the API reports wells or record identity (older APIs carry neither).
    const showWellColumns = hasWells || hasRecordIdentity;
    const isColumnAvailable = (col: { field: string }) => showWellColumns || !WELL_FIELDS.includes(col.field);
    const visibleColumns = columns.filter((col) => col.visible && isColumnAvailable(col));
    // Phone: the RTU name column leads the table as the expander column (toggler + checkbox + name share one cell) and
    // the Producer column, which is empty for RTU rows, moves behind the data columns (its filter stays reachable).
    // If the name column is hidden in the column picker the layout is the desktop one.
    const nameFirst = isMobile && visibleColumns.some((col) => col.field === 'name');
    // header cells in order (desktop only: phones keep the auto layout), for a drag that would take a column below its min width
    resizeFieldsRef.current = isMobile ? [] : ['provider', ...visibleColumns.map((col) => col.field), undefined];

    // Desktop widths: each column is as wide as its longest value (header, or any row of the whole data set, plus the
    // group labels with their toggler / checkbox / indent in the first column, capped), the table scrolls sideways when
    // they do not fit and shares out spare room when they do (see resolveColumnWidths).
    const visibleKey = visibleColumns.map((col) => `${col.field}:${String(col.header)}`).join('|');
    const contentWidths = useContentWidths({
        enabled: !isMobile,
        rootRef,
        deps: [augmentedRows, visibleKey, clusterMode],
        compute: (measurer) => {
            const accumulator = new WidthAccumulator(measurer);
            accumulator.header('provider', t('assetList.columns.provider'));
            visibleColumns.forEach((col) => accumulator.header(col.field, String(col.header)));
            const providerLabels = new Set<string>();
            const fieldLabels = new Set<string>();
            augmentedRows.forEach((row) => {
                providerLabels.add(isMissing(row.provider) ? UNASSIGNED_GROUP_LABEL : String(row.provider));
                fieldLabels.add(isMissing(row.field) ? UNASSIGNED_GROUP_LABEL : String(row.field));
                accumulator.value('provider', '', producerExtra(2)); // the toggler and checkbox of an RTU row
                visibleColumns.forEach((col) => {
                    const content = cellContent(row, col.field, clusterMode, measurer);
                    if (content) {
                        accumulator.value(col.field, content.text, content.extra, content.bold);
                    }
                });
            });
            providerLabels.forEach((label) => accumulator.value('provider', label, producerExtra(0)));
            fieldLabels.forEach((label) => accumulator.value('provider', label, producerExtra(1)));
            return accumulator.result();
        },
    });
    contentWidthsRef.current = contentWidths;
    const wrapperWidth = useWrapperWidth(rootRef, '.p-treetable-wrapper', !isMobile);
    const resolved = resolveColumnWidths({
        fields: ['provider', ...visibleColumns.map((col) => col.field)],
        content: contentWidths,
        manual: columnWidthOverrides,
        containerWidth: wrapperWidth,
        fixedPx: ACTIONS_COLUMN_WIDTH,
    });
    // PrimeReact sets the table width itself while a drag ends; React's value is the one that counts
    useLayoutEffect(() => {
        const table = rootRef.current?.querySelector('.p-treetable-table, .p-treetable table') as HTMLElement | null;
        if (table && !isMobile) {
            table.style.width = `${resolved.total}px`;
        }
    });

    const onColumnsChange = useCallback((field:any, isChecked:any) => {
        touchedColumnsRef.current.add(String(field));
        forgetWidth(String(field));
        setColumns((currentColumns) => {
            const visibleCount = currentColumns.filter((col) => col.visible).length;
            const isLastVisibleColumn =
                !isChecked && visibleCount === 1 && currentColumns.find((col) => col.field === field)?.visible;
            if (isLastVisibleColumn) {
                return currentColumns;
            }
            return currentColumns.map(col => {
                if (col.field === field) {
                    return { ...col, visible: isChecked };
                }
                return col;
            });
        });
    }, [forgetWidth]);

    const handleSelectionChange = useCallback((e: any) => {
        const nextKeys = e.value ?? null;
        setSelectedNodeKeys(nextKeys);
    }, []);

    const op:any = useRef(null);
    const renderHeader = () => {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '0.6rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: isMobile && showFilters ? '1 1 100%' : 1, minWidth: 0 }}>
                    {showFilters && (
                        <div
                            className="asset-toolbar__search"
                            style={isMobile ? { display: 'flex', flex: 1, minWidth: 0 } : { display: 'flex', justifyContent: 'center', maxWidth: '200px' }}
                        >
                            <span className="p-input-icon-left" style={{ display: 'inline-flex', alignItems: 'center', width: '100%' }}>
                                <i className="pi pi-search" style={{ fontSize: '0.7rem', left: '0.5rem' }} />
                                <InputText 
                                    type="search" 
                                    value={globalFilter}
                                    onInput={(e) => {
                                        setGlobalFilter((e.target as HTMLInputElement).value);
                                        setFirst(0);
                                    }}
                                    placeholder="" 
                                    aria-label={t('assetList.searchRtus', { defaultValue: 'Search RTUs' })}
                                    style={{ fontSize: '0.75rem', height: '1.5rem', paddingLeft: '1.5rem', width: '100%' }}
                                />
                            </span>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'flex-end', marginLeft: 'auto', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <i
                            className="pi pi-search"
                            style={{ cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: showFilters ? defaultThemeColor : '#333', padding: '0.25rem' }}
                            title={showFilters ? t('search.hide') : t('search.show')}
                            onClick={toggleFilters}
                        />
                        <i
                            className="pi pi-list"
                            style={{ cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700, color: '#333', padding: '0.25rem' }}
                            title={t('table.selectColumns')}
                            onClick={(e) => op.current.toggle(e)}
                        />
                        <OverlayPanel ref={op} closeOnEscape dismissable={true} className="asset-column-picker">
                            {columns.filter(isColumnAvailable).map((col) => (
                                <div key={col.field} className="asset-column-picker__row">
                                    <Checkbox
                                        inputId={`cb-${col.field}`}
                                        checked={col.visible}
                                        onChange={(e) => onColumnsChange(col.field, e.checked)}
                                        className="asset-column-picker__checkbox"
                                    />
                                    <label htmlFor={`cb-${col.field}`} className="asset-column-picker__label">{col.header}</label>
                                </div>
                            ))}
                            {hasFormerWells && (
                              <div className="asset-column-picker__section">
                                <div className="asset-column-picker__section-title">{t('assetList.view', { defaultValue: 'View' })}</div>
                                <div className="asset-column-picker__row">
                                  <Checkbox
                                    inputId="asset-include-former-wells"
                                    checked={includeFormer}
                                    onChange={(e) => {
                                      setIncludeFormer(Boolean(e.checked));
                                      setFirst(0);
                                    }}
                                    className="asset-column-picker__checkbox"
                                  />
                                  <label htmlFor="asset-include-former-wells" className="asset-column-picker__label">
                                    {t('assetList.includeFormerWells', { defaultValue: 'Include former wells' })}
                                  </label>
                                </div>
                              </div>
                            )}
                        </OverlayPanel>
                        {isMobile && (
                        <button
                          type="button"
                          className="asset-select-toggle"
                          aria-label={t('assetList.selectMode', { defaultValue: 'Select RTUs' })}
                          title={t('assetList.selectMode', { defaultValue: 'Select RTUs' })}
                          aria-pressed={selectMode}
                          onClick={toggleSelectMode}
                          style={{ color: selectMode ? defaultThemeColor : '#333' }}
                        >
                          <i className="pi pi-check-square" />
                        </button>
                        )}
                    </div>
                    {showSelection && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', minWidth: 'max-content' }}>
                            <BulkSelectionCount count={selectedRows.length}><RTUActions mode="bulk" selectedRows={selectedRows} selectedCount={selectedRows.length} /></BulkSelectionCount>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const header = renderHeader();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchRTUs({}));
      }, [dispatch]);

    useEffect(() => {
        setColumns((currentColumns) =>
            hydrateColumnVisibility(currentColumns, columnStorageKeyRef.current)
        );
    }, []);

    // Only columns the user toggled are stored (merged), so defaults never masquerade as a saved choice.
    useEffect(() => {
        persistToggledColumns(columns, touchedColumnsRef.current, columnStorageKeyRef.current);
    }, [columns]);

    // Desktop: fixed layout with explicit column widths (the table is exactly as wide as its columns, so a drag changes
    // only the dragged column and cell content never sets a width). Phones keep the auto layout.
    const desktopTableStyle = isMobile
        ? undefined
        : // width only: PrimeReact's expand mode sets the table width itself on a drag, and a min-width here would fight a shrink
          { tableLayout: 'fixed' as const, width: `${resolved.total}px` };

    // Columns that should be centered (numeric, percentage, status, icons)
    const centerCols = ['status', 'id', 'diskUsage', 'cpuUsage', 'groupId'];

    // The expander column hosts the row toggler and the selection checkbox; `rowCheckboxPt` names the checkbox.
    const producerColumn = (isExpander: boolean) => (
        <Column
            key="provider"
            field="provider"
            header={t('assetList.columns.provider')}
            expander={isExpander}
            filter={showFilters}
            showFilterMenu={false}
            filterElement={columnTextFilter('provider', t('assetList.columns.provider'))}
            style={isMobile ? { whiteSpace: 'nowrap' } : { whiteSpace: 'nowrap', ...widthStyle('provider', resolved.widths, contentWidths) }}
            bodyClassName={isExpander && !isMobile ? 'rtu-tree-first-cell' : undefined}
            body={(node) => nodeTemplate(node, 'provider')}
            {...(isExpander ? ({ pt: { rowCheckbox: rowCheckboxPt } } as any) : {})}
        />
    );
    const renderDataColumn = (col: (typeof columns)[number], isFirst: boolean) => {
        const isCenter = centerCols.includes(col.field);

        return (
            <Column
                key={col.field}
                field={col.field}
                header={col.header}
                expander={isFirst || col.expander}
                filterElement={col.field === 'status' ? statusFilterElement : (col.filterElement ?? columnTextFilter(col.field, col.header, col.filterMatchMode))}
                style={isMobile ? { whiteSpace: 'nowrap' } : { whiteSpace: 'nowrap', ...widthStyle(col.field, resolved.widths, contentWidths) }}
                bodyStyle={{ textAlign: isCenter ? 'center' : 'left' }}
                bodyClassName={isFirst ? 'rtu-tree-first-cell' : undefined}
                filter={showFilters}
                showFilterMenu={false}
                sortable
                headerClassName={col.headerClassName}
                filterMatchMode={col.filterMatchMode}
                body={(node: any) => nodeTemplate(node, col.field)}
                {...(isFirst ? ({ pt: { rowCheckbox: rowCheckboxPt } } as any) : {})}
            />
        );
    };

    return (
        <div ref={rootRef} className="card" style={{ padding: '0.25rem', overflow: 'hidden' }} onMouseDownCapture={onMouseDownCapture}>
            <h1 className="dms-visually-hidden">{t('navigation.groupedAssets')}</h1>
            <TreeTable
                value={treeData}
                rowClassName={(node: any) => {
                    const id = Number(node?.data?.id);
                    const info = clusterMode && node?.data?.id && node.data.recordState !== 'FORMER' ? clusterInfo[id] : undefined;
                    if (!info) {
                        return {};
                    }
                    const isMaster = info.depth === 0 && info.childCount > 0;
                    const isChild = info.depth === 1;
                    return {
                        'rtu-cluster-row--master': isMaster,
                        'rtu-cluster-row--child': isChild,
                        'rtu-cluster-row--last': (isChild && info.isLastChild) || (isMaster && !info.expanded),
                    };
                }}
                resizableColumns
                columnResizeMode="expand"
                onColumnResizeEnd={onColumnResizeEnd}
                header={header}
                lazy
                paginator
                first={treeFirst}
                rows={pageSize}
                totalRecords={fullTreeData.length}
                onPage={(e: any) => {
                    setFirst(e.first);
                    setPageSize(e.rows);
                }}
                filters={columnFilters as any}
                onFilter={(e: any) => {
                    setColumnFilters(e.filters ?? {});
                    setFirst(0);
                }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                paginatorLeft={<PaginatorRowsLabel />}
                paginatorTemplate={STANDARD_PAGINATOR_TEMPLATE}
                currentPageReportTemplate={STANDARD_PAGE_REPORT_TEMPLATE}
                paginatorClassName="compact-paginator"
                showGridlines
                removableSort
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={(e: any) => {
                    // the click that ends a column drag is part of the drag; it must not sort
                    if (shouldIgnoreSort()) {
                        return;
                    }
                    setSortField(e.sortField);
                    setSortOrder(e.sortOrder);
                    setFirst(0);
                }}
                selectionMode={showSelection ? 'checkbox' : undefined}
                togglerTemplate={togglerTemplate}
                selectionKeys={selectedNodeKeys}
                onSelectionChange={handleSelectionChange}
                style={{ width: '100%' }}
                tableStyle={desktopTableStyle ?? { tableLayout: 'auto', width: 'auto', minWidth: '100%' }}
                emptyMessage={loading ? (
                    <SkeletonRegion label={t('common.loading', { defaultValue: 'Loading' })}>
                        <SkeletonTree rows={10} columns={[0.7, 0.6, 1.2, 0.7, 0.7, 1, 1, 1.4]} />
                    </SkeletonRegion>
                ) : undefined}
            >
                {!nameFirst && producerColumn(true)}
                {(nameFirst
                    ? [...visibleColumns.filter((col) => col.field === 'name'), ...visibleColumns.filter((col) => col.field !== 'name')]
                    : visibleColumns
                ).map((col) => renderDataColumn(col, nameFirst && col.field === 'name'))}
                {nameFirst && producerColumn(false)}
                <Column 
                    body={rowButtonTemplate} 
                    filter={showFilters} 
                    filterElement={() => <span></span>}
                    header={t('table.actions')} 
                    headerClassName="grouped-assets-actions-column"
                    {...({ filterHeaderClassName: 'grouped-assets-actions-column' } as any)}
                    bodyClassName="grouped-assets-actions-column"
                    style={isMobile ? { width: '40px', minWidth: '40px', maxWidth: '40px' } : { width: `${ACTIONS_COLUMN_WIDTH}px`, minWidth: `${ACTIONS_COLUMN_WIDTH}px`, maxWidth: `${ACTIONS_COLUMN_WIDTH}px` }}
                    {...({ pt: NO_RESIZER_PT } as any)}
                    headerStyle={isMobile ? { textAlign: 'center' } : { textAlign: 'center', width: `${ACTIONS_COLUMN_WIDTH}px`, minWidth: `${ACTIONS_COLUMN_WIDTH}px`, maxWidth: `${ACTIONS_COLUMN_WIDTH}px` }}
                    bodyStyle={{ textAlign: 'center', overflow: 'visible' }}
                />
            </TreeTable>
        </div>
    );
};

export default RTUTreeList;
